import { useEffect, useState, useCallback, useMemo } from 'react';
import { getTrainingAdminCategories, GetTrainingAdminCategoriesOutputType, saveTrainingCategory } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit, ChevronDown, ChevronRight, FolderOpen, Archive, RotateCcw, Merge, Trash2, Search, AlertTriangle, List, GitBranch, X, Loader2, Eye, Info } from 'lucide-react';
import { toast } from 'sonner';

type Category = GetTrainingAdminCategoriesOutputType['categories'][0];
type ContentType = GetTrainingAdminCategoriesOutputType['contentTypes'][0];

export default function CategoryManagerSection() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');

  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [colour, setColour] = useState('#A36B58');
  const [parentId, setParentId] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  // Merge state
  const [merging, setMerging] = useState<Category | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');

  // Delete confirm
  const [deleting, setDeleting] = useState<Category | null>(null);

  // Content types panel
  const [showContentTypes, setShowContentTypes] = useState(false);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    getTrainingAdminCategories({ includeArchived: showArchived, search: search || undefined })
      .then(d => {
        setCategories(d.categories);
        setContentTypes(d.contentTypes);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [showArchived, search]);

  useEffect(() => { load(); }, [load]);

  const topLevel = useMemo(() =>
    categories.filter(c => !c.parentCategory).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)),
    [categories]
  );

  const childrenOf = useCallback((id: string) =>
    categories.filter(c => c.parentCategory === id).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)),
    [categories]
  );

  const openCreate = (parentCategoryId?: string) => {
    setEditing(null);
    setName('');
    setDescription('');
    setColour('#A36B58');
    setParentId(parentCategoryId || '');
    setDisplayOrder(categories.length + 1);
    setShowEditor(true);
  };

  const openEdit = (cat: Category) => {
    setEditing(cat);
    setName(cat.categoryName);
    setDescription(cat.description || '');
    setColour(cat.colour || '#A36B58');
    setParentId(cat.parentCategory || '');
    setDisplayOrder(cat.displayOrder || 0);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await saveTrainingCategory({
        action: editing ? 'update' : 'create',
        id: editing?.id,
        categoryName: name,
        description: description || undefined,
        colour,
        parentCategory: parentId || '',
        displayOrder,
      });
      toast.success(editing ? 'Category updated' : 'Category created');
      setShowEditor(false);
      load();
    } catch (err: any) { toast.error(err?.message || 'Failed to save'); }
    setSaving(false);
  };

  const handleArchive = async (cat: Category) => {
    try {
      await saveTrainingCategory({ action: 'archive', id: cat.id });
      toast.success(`"${cat.categoryName}" archived`);
      load();
    } catch (err: any) { toast.error(err?.message || 'Failed to archive'); }
  };

  const handleRestore = async (cat: Category) => {
    try {
      await saveTrainingCategory({ action: 'restore', id: cat.id });
      toast.success(`"${cat.categoryName}" restored`);
      load();
    } catch (err: any) { toast.error(err?.message || 'Failed to restore'); }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await saveTrainingCategory({ action: 'delete', id: deleting.id });
      toast.success(`"${deleting.categoryName}" deleted`);
      setDeleting(null);
      load();
    } catch (err: any) {
      toast.error(err?.message || 'Cannot delete');
      setDeleting(null);
    }
  };

  const handleMerge = async () => {
    if (!merging || !mergeTargetId) return;
    try {
      const result = await saveTrainingCategory({ action: 'merge', id: merging.id, mergeIntoId: mergeTargetId });
      toast.success(result.message || 'Categories merged');
      setMerging(null);
      setMergeTargetId('');
      load();
    } catch (err: any) { toast.error(err?.message || 'Merge failed'); }
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Tree view renderer
  const renderCatRow = (cat: Category, level: number = 0) => {
    const children = childrenOf(cat.id);
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(cat.id);
    const isArchived = cat.archived;

    return (
      <div key={cat.id}>
        <div
          className={`flex items-center gap-2 px-3 py-2.5 border border-border rounded-lg bg-card group hover:border-primary/20 transition-colors ${isArchived ? 'opacity-60' : ''}`}
          style={{ marginLeft: level * 28 }}
        >
          {hasChildren ? (
            <button onClick={() => toggleExpand(cat.id)} className="text-muted-foreground shrink-0 w-5 h-5 flex items-center justify-center">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : <span className="w-5 shrink-0" />}

          <div className="w-3 h-3 rounded-full shrink-0 border border-border" style={{ backgroundColor: cat.colour || 'hsl(var(--primary))' }} />

          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-foreground">{cat.categoryName}</span>
            {cat.description && <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">{cat.description}</span>}
          </div>

          <span className="text-xs text-muted-foreground shrink-0">{cat.itemCount} item{cat.itemCount !== 1 ? 's' : ''}</span>

          {isArchived && <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">Archived</span>}

          <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => openEdit(cat)} className="p-1 text-muted-foreground hover:text-foreground" title="Edit"><Edit className="w-3.5 h-3.5" /></button>
            <button onClick={() => openCreate(cat.id)} className="p-1 text-muted-foreground hover:text-foreground" title="Add child"><Plus className="w-3.5 h-3.5" /></button>
            {!isArchived && (
              <button onClick={() => handleArchive(cat)} className="p-1 text-muted-foreground hover:text-foreground" title="Archive"><Archive className="w-3.5 h-3.5" /></button>
            )}
            {isArchived && (
              <button onClick={() => handleRestore(cat)} className="p-1 text-muted-foreground hover:text-foreground" title="Restore"><RotateCcw className="w-3.5 h-3.5" /></button>
            )}
            <button onClick={() => setMerging(cat)} className="p-1 text-muted-foreground hover:text-foreground" title="Merge"><Merge className="w-3.5 h-3.5" /></button>
            {cat.itemCount === 0 && cat.children.length === 0 && (
              <button onClick={() => setDeleting(cat)} className="p-1 text-muted-foreground hover:text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        </div>
        {isExpanded && children.map(child => renderCatRow(child, level + 1))}
      </div>
    );
  };

  if (loading) return <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-foreground">Categories & Content Taxonomy</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowContentTypes(true)}>
            <Info className="w-4 h-4 mr-1" /> Content Types
          </Button>
          <Button size="sm" onClick={() => openCreate()}>
            <Plus className="w-4 h-4 mr-1" /> New Category
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search categories…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex gap-1 border border-border rounded-md p-0.5">
          <button onClick={() => setViewMode('tree')} className={`px-2 py-1 text-xs rounded ${viewMode === 'tree' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <GitBranch className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setViewMode('list')} className={`px-2 py-1 text-xs rounded ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <List className="w-3.5 h-3.5" />
          </button>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="rounded border-border" />
          Show archived
        </label>
        <span className="text-xs text-muted-foreground">{categories.length} categories ({categories.filter(c => c.archived).length} archived)</span>
      </div>

      {/* Category list/tree */}
      {categories.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-50" />
          <p className="text-sm text-muted-foreground">{search ? 'No categories match your search' : 'No categories yet'}</p>
          {!search && <Button size="sm" variant="outline" className="mt-3" onClick={() => openCreate()}>Create first category</Button>}
        </div>
      ) : viewMode === 'tree' ? (
        <div className="space-y-1">
          {topLevel.map(cat => renderCatRow(cat))}
        </div>
      ) : (
        <div className="space-y-1">
          {categories.sort((a, b) => (a.categoryName || '').localeCompare(b.categoryName || '')).map(cat => (
            <div key={cat.id} className={`flex items-center gap-2 px-3 py-2.5 border border-border rounded-lg bg-card group hover:border-primary/20 ${cat.archived ? 'opacity-60' : ''}`}>
              <div className="w-3 h-3 rounded-full shrink-0 border border-border" style={{ backgroundColor: cat.colour || 'hsl(var(--primary))' }} />
              <span className="text-sm font-medium text-foreground flex-1">{cat.categoryName}</span>
              {cat.parentCategory && (
                <span className="text-xs text-muted-foreground">
                  ↳ {categories.find(p => p.id === cat.parentCategory)?.categoryName}
                </span>
              )}
              <span className="text-xs text-muted-foreground">{cat.itemCount} items</span>
              {cat.archived && <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Archived</span>}
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(cat)} className="p-1 text-muted-foreground hover:text-foreground"><Edit className="w-3.5 h-3.5" /></button>
                {!cat.archived ? <button onClick={() => handleArchive(cat)} className="p-1 text-muted-foreground hover:text-foreground"><Archive className="w-3.5 h-3.5" /></button>
                  : <button onClick={() => handleRestore(cat)} className="p-1 text-muted-foreground hover:text-foreground"><RotateCcw className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Category' : 'New Category'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Category Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Food Safety" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this category cover?" rows={2} />
            </div>
            <div className="flex gap-4">
              <div>
                <Label>Colour</Label>
                <div className="flex items-center gap-2">
                  <Input type="color" value={colour} onChange={e => setColour(e.target.value)} className="h-10 w-14 p-1 cursor-pointer" />
                  <span className="text-xs text-muted-foreground">{colour}</span>
                </div>
              </div>
              <div className="flex-1">
                <Label>Display Order</Label>
                <Input type="number" value={displayOrder} onChange={e => setDisplayOrder(Number(e.target.value))} min={0} />
              </div>
            </div>
            <div>
              <Label>Parent Category</Label>
              <Select value={parentId || 'none'} onValueChange={v => setParentId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top level)</SelectItem>
                  {categories.filter(c => c.id !== editing?.id && !c.archived).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.categoryName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button disabled={!name.trim() || saving} onClick={handleSave}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={!!merging} onOpenChange={open => { if (!open) setMerging(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Merge Category</DialogTitle></DialogHeader>
          <div className="py-2">
            <div className="bg-muted/50 border border-border rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
                <p className="text-sm text-foreground">
                  Merging "<strong>{merging?.categoryName}</strong>" will reassign all its content and child categories to the target, then archive it.
                </p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">This action creates an audit record and can be reviewed but is not easily reversed.</p>
            </div>
            <Label>Merge into:</Label>
            <Select value={mergeTargetId || 'pick'} onValueChange={v => setMergeTargetId(v === 'pick' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select target category…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pick" disabled>Select target category…</SelectItem>
                {categories.filter(c => c.id !== merging?.id && !c.archived).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.categoryName} ({c.itemCount} items)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMerging(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!mergeTargetId} onClick={handleMerge}>
              <Merge className="w-4 h-4 mr-1" /> Merge & Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleting} onOpenChange={open => { if (!open) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleting?.categoryName}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the category. This is only allowed when no content or child categories are assigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Content Types Info Dialog */}
      <Dialog open={showContentTypes} onOpenChange={setShowContentTypes}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Content Types (System-Managed)</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground mb-3">
            Content types describe <strong>what an item is</strong> (e.g. SOP, Policy, Training Module). They are separate from categories, which describe <strong>what subject it covers</strong> (e.g. Food Safety, Bar). Content types are system-managed — labels can be customised but structural types cannot be deleted while in use.
          </p>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {contentTypes.map(ct => (
              <div key={ct.value} className="flex items-center justify-between px-3 py-2 border border-border rounded-lg">
                <span className="text-sm text-foreground">{ct.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{ct.inUseCount} in use</span>
                  {ct.inUseCount > 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">Protected</span>}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContentTypes(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
